import { z } from 'zod';
import { createModel, QueryMany, QueryOne, statusCommonSchema } from '../../common';
import { businessSchema, productSchema } from '../../common/app-registry.schema';
import { dateSchema, nameSchema, objectIdSchema, positiveIntegerSchema } from '../../validation';
import { referralLinkAttributionSchema } from './referral.attribution.model';
import { referralCodeSchema } from './referral.code.schema';
import { referralRewardRuleSchema } from './referral.reward-rule.model';

/**
 * Denormalised hit / outcome counters kept on the link so the admin
 * list renders without an aggregation per row. Bumped atomically by the
 * tracking, lead and reward services; `ReferralTracking`, `Lead` and
 * `ReferralReward` remain the source of truth and a nightly recount can
 * rebuild these from them.
 */
export const referralLinkStatsSchema = z.object({
  /** Deduped page views (`ReferralTracking.type === 'view'`). */
  views: positiveIntegerSchema.default(0),
  /** Deduped CTA clicks (`ReferralTracking.type === 'click'`). */
  clicks: positiveIntegerSchema.default(0),
  /** Leads attributed to this link, any mode. */
  leads: positiveIntegerSchema.default(0),
  /** Attributed leads that created an account. */
  accounts: positiveIntegerSchema.default(0),
  /** Attributed accounts with at least one settled Billing. */
  paid: positiveIntegerSchema.default(0),
  /** Reward rows created for this link. */
  rewards: positiveIntegerSchema.default(0),
  /** When a hit was last recorded. */
  lastHitAt: dateSchema.optional(),
});
export type ReferralLinkStats = z.infer<typeof referralLinkStatsSchema>;

/**
 * `ReferralLink` collection — one shareable code / URL owned by a
 * `ReferralPartner`.
 *
 * A partner has many links live at once: one per campaign, per product,
 * per channel. Each has a `code` (what goes in `?r=`), a `title` and
 * `description` for the partner and the admin, an optional
 * product / business target, an attribution config (how leads get
 * matched to it) and an optional reward override.
 *
 * Expected indexes (created by the `referral-collections` migration):
 * - `{ code: 1 }` unique — the resolve path on every `?r=`.
 * - `{ partnerId: 1, 'doc.status': 1 }` — partner's links.
 * - `{ 'doc.status': 1, 'attribution.modes': 1 }` — closest-tracking
 *   candidate links.
 */
export const ReferralLinkSchema = createModel({
  /** Owning `ReferralPartner._id`. */
  partnerId: objectIdSchema,
  /**
   * The public code — `?r=CODE`, typed into WhatsApp, printed on a
   * flyer. Unique across the collection, upper-case, 4–16 alphanumerics.
   * Generated by the backend when the admin leaves it blank.
   */
  code: referralCodeSchema,
  /** Short label — "Instagram bio", "Riyadh expo flyer", "WhatsApp broadcast May". */
  title: nameSchema,
  /** Longer note on where / how the link is used. */
  description: z.string().trim().optional(),
  /**
   * What this link sells. Optional targeting: a link for Wathiqa Plus
   * freelancers lands its leads in that funnel and its `wa.me` prefill
   * points at that brand's line. `business` is derivable from
   * `product` when both are set; it is stored so a business-wide link
   * ("anything Wathiqa Plus") can exist without naming a product.
   */
  business: businessSchema.optional(),
  product: productSchema.optional(),
  /**
   * Where the link sends people. Absent = the business landing page.
   * Stored as a path or absolute URL; the backend appends `?r=CODE`.
   */
  destination: z.string().trim().optional(),
  /** How leads get matched to this link. */
  attribution: referralLinkAttributionSchema.default({}),
  /**
   * Per-link reward override. Absent = inherit the partner's
   * `reward`. Set to `{ parts: [] }` to make a link tracking-only while
   * the partner's other links still pay.
   */
  reward: referralRewardRuleSchema.optional(),
  /** Denormalised counters. See {@link referralLinkStatsSchema}. */
  stats: referralLinkStatsSchema.default({}),
  /**
   * `active` links resolve; anything else is kept for history but a
   * new `?r=` hit on it records nothing and attributes nothing.
   */
  status: statusCommonSchema,
  /** Optional hard stop — the link stops resolving after this date. */
  expiresAt: dateSchema.optional(),
});

export type ReferralLink = z.infer<typeof ReferralLinkSchema.model>;
export type ReferralLinkData = z.infer<typeof ReferralLinkSchema.data>;
export type ReferralLinkPartial = z.infer<typeof ReferralLinkSchema.partial>;
export type ReferralLinkQueryMany = QueryMany<ReferralLink>;
export type ReferralLinkQueryOne = QueryOne<ReferralLink>;
